/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import CssBaseline from '@mui/material/CssBaseline';
import packageInfo from '../package.json';
import { AppRouter } from 'AppRouter';
import Notification from './components/Notification/Notification';
import { ThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from './assets/style/them';
import { useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from './data/store.typings';
import defineAppAbilityByPermits, { AppAbilityContext } from './accessibility/ability';
import DatePickerLocalizationProvider from './providers/DatePickerLocalizationProvider/DatePickerLocalizationProvider';

const useStyles = makeStyles(() =>
    createStyles({
        version: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            margin: 0,
            color: theme.palette.altText.main,
            opacity: 0,
            '&:hover': {
                opacity: 1,
            },
        },
    }),
);

const App = () => {
    const classes = useStyles();
    const permits = useSelector((state: RootState) => state.userReducer.authData?.permits, shallowEqual);
    const appAbility = useMemo(() => defineAppAbilityByPermits(permits ?? []), [permits]);

    useEffect(() => {
        const favicon = document.getElementById('favicon');

        if (import.meta.env.VITE_APP_VARIANT === 'nooga') {
            document.title = 'nooga';
            favicon.setAttribute('href', './faviconNooga.png');
        } else {
            document.title = 'e-kassa';
            favicon.setAttribute('href', './faviconAZN.png');
        }
    }, []);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <DatePickerLocalizationProvider>
                    <div
                        id="app-content-id"
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            background: theme.palette.mainBackground.main,
                        }}
                    >
                        <CssBaseline />
                        <Notification />

                        <AppAbilityContext.Provider value={appAbility}>
                            <AppRouter />
                        </AppAbilityContext.Provider>

                        <p className={classes.version}>{`v. ${packageInfo.version}`}</p>
                    </div>
                </DatePickerLocalizationProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default App;
